import FileSaver from 'file-saver';
import XLSX from 'xlsx';
import type {Range, WorkSheet} from 'xlsx';

export const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const EXCEL_TYPE_WITH_CHARSET = `${EXCEL_TYPE};charset=UTF-8`;
export const EXCEL_EXTENSION = '.xlsx';

export const normalizeExcelFileNamePart = (value: unknown, options: {lowercase?: boolean; separator?: string} = {}) => {
  const {lowercase = false, separator = '-'} = options;
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split('')
    .filter((character) => character.charCodeAt(0) > 31)
    .join('')
    .replace(/[<>:"/\\|?*]/g, ' ')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, separator)
    .replace(new RegExp(`${escapedSeparator}{2,}`, 'g'), separator)
    .replace(/^[.\s_-]+|[.\s_-]+$/g, '');
  return lowercase ? normalized.toLowerCase() : normalized;
};

export type ExcelCellValue = string | number | boolean | Date | null | undefined;

export type ExcelTableHeaderCell =
  | ExcelCellValue
  | {
      value: ExcelCellValue;
      colSpan?: number;
      rowSpan?: number;
    };

export type ExcelTableBlock = {
  title?: string;
  headers: ExcelTableHeaderCell[][];
  rows: ExcelCellValue[][];
};

type ExcelTableBounds = {
  endRow: number;
  width: number;
};

const normalizeSpan = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : 1;
};

const getHeaderCell = (cell: ExcelTableHeaderCell) => {
  if (cell && typeof cell === 'object' && !(cell instanceof Date) && 'value' in cell) {
    return {
      value: cell.value,
      colSpan: normalizeSpan(cell.colSpan),
      rowSpan: normalizeSpan(cell.rowSpan),
    };
  }
  return {value: cell, colSpan: 1, rowSpan: 1};
};

const expandHeaderRows = (headers: ExcelTableHeaderCell[][]) => {
  const occupied: boolean[][] = headers.map(() => []);
  const rows: ExcelCellValue[][] = headers.map(() => []);
  const merges: Range[] = [];
  let width = 0;

  headers.forEach((headerRow, rowIndex) => {
    let columnIndex = 0;
    headerRow.forEach((rawCell) => {
      while (occupied[rowIndex][columnIndex]) columnIndex += 1;
      const cell = getHeaderCell(rawCell);
      rows[rowIndex][columnIndex] = cell.value;

      for (let rowOffset = 0; rowOffset < cell.rowSpan; rowOffset += 1) {
        const targetRow = rowIndex + rowOffset;
        if (targetRow >= headers.length) break;
        occupied[targetRow] = occupied[targetRow] || [];
        rows[targetRow] = rows[targetRow] || [];
        for (let columnOffset = 0; columnOffset < cell.colSpan; columnOffset += 1) {
          occupied[targetRow][columnIndex + columnOffset] = true;
          if (rowOffset !== 0 || columnOffset !== 0) {
            rows[targetRow][columnIndex + columnOffset] = '';
          }
        }
      }

      const endRow = Math.min(headers.length - 1, rowIndex + cell.rowSpan - 1);
      const endColumn = columnIndex + cell.colSpan - 1;
      if (endRow > rowIndex || endColumn > columnIndex) {
        merges.push({
          s: {r: rowIndex, c: columnIndex},
          e: {r: endRow, c: endColumn},
        });
      }
      width = Math.max(width, endColumn + 1);
      columnIndex = endColumn + 1;
    });
  });

  rows.forEach((row) => {
    while (row.length < width) row.push('');
  });

  return {rows, merges, width};
};

export const getExcelTableBlockWidth = (table: ExcelTableBlock) => {
  const headerWidth = expandHeaderRows(table.headers).width;
  const bodyWidth = table.rows.reduce((max, row) => Math.max(max, row.length), 0);
  return Math.max(1, headerWidth, bodyWidth);
};

export class ExcelWorksheetBuilder {
  private worksheet: WorkSheet = XLSX.utils.aoa_to_sheet([]);
  private merges: Range[] = [];
  private columnWidths: number[] = [];

  addRows(rows: ExcelCellValue[][], startRow: number, startColumn = 0) {
    if (!rows.length) return;
    XLSX.utils.sheet_add_aoa(this.worksheet, rows, {
      origin: {r: Math.max(0, startRow), c: Math.max(0, startColumn)},
    });
  }

  addMergedValue(value: ExcelCellValue, row: number, startColumn: number, endColumn: number) {
    this.addRows([[value]], row, startColumn);
    if (endColumn > startColumn) {
      this.mergeCells(row, startColumn, row, endColumn);
    }
  }

  mergeCells(startRow: number, startColumn: number, endRow: number, endColumn: number) {
    this.merges.push({
      s: {r: startRow, c: startColumn},
      e: {r: endRow, c: endColumn},
    });
  }

  setColumnWidth(column: number, width: number) {
    const normalizedWidth = Math.max(4, Math.min(60, width));
    this.columnWidths[column] = Math.max(this.columnWidths[column] || 0, normalizedWidth);
  }

  fitColumns(rows: ExcelCellValue[][], startColumn = 0, minWidth = 10, maxWidth = 36) {
    rows.forEach((row) => {
      row.forEach((value, columnOffset) => {
        const longestLine = String(value ?? '')
          .split(/\r?\n/)
          .reduce((max, line) => Math.max(max, line.length), 0);
        this.setColumnWidth(startColumn + columnOffset, Math.max(minWidth, Math.min(maxWidth, longestLine + 2)));
      });
    });
  }

  addTable(table: ExcelTableBlock, startRow: number, startColumn = 0): ExcelTableBounds {
    const expandedHeaders = expandHeaderRows(table.headers);
    const width = Math.max(
      1,
      expandedHeaders.width,
      table.rows.reduce((max, row) => Math.max(max, row.length), 0),
    );
    let currentRow = startRow;

    if (table.title) {
      this.addMergedValue(table.title, currentRow, startColumn, startColumn + width - 1);
      currentRow += 1;
    }

    this.addRows(expandedHeaders.rows, currentRow, startColumn);
    expandedHeaders.merges.forEach((merge) => {
      this.mergeCells(currentRow + merge.s.r, startColumn + merge.s.c, currentRow + merge.e.r, startColumn + merge.e.c);
    });
    currentRow += expandedHeaders.rows.length;

    this.addRows(table.rows, currentRow, startColumn);
    this.fitColumns([...expandedHeaders.rows, ...table.rows], startColumn);
    currentRow += table.rows.length;

    return {
      endRow: Math.max(startRow, currentRow - 1),
      width,
    };
  }

  build() {
    if (this.merges.length) {
      this.worksheet['!merges'] = this.merges;
    }
    if (this.columnWidths.length) {
      this.worksheet['!cols'] = this.columnWidths.map((width) => ({wch: width || 10}));
    }
    return this.worksheet;
  }
}

export class Excel {
  static exportAsExcelFile(json, header, fileName, options: any) {
    if (options) {
      //console.log('%c⧭ exportAsExcelFile options', 'color: #b81c1c', fileName, options);
    }

    let worksheet = XLSX.utils.json_to_sheet(json, {
      header,
      skipHeader: false,
    });

    let workbook = {
      Sheets: {data: worksheet},
      SheetNames: ['data'],
    };

    if (options?.appendSheets) {
      options.appendSheets.forEach((sheet) => {
        let {name, json, header, skipHeader = false} = sheet;
        let newSheet = XLSX.utils.json_to_sheet(json, {
          header,
          skipHeader,
        });
        workbook.Sheets[name] = newSheet;
        if (!workbook.SheetNames.includes(name)) {
          workbook.SheetNames.push(name);
        }
      });
    }

    let excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveAsExcelFile(excelBuffer, fileName);
  }

  static exportWorksheetAsExcelFile(worksheet: WorkSheet, sheetName: string, fileName: string) {
    const workbook = XLSX.utils.book_new();
    const normalizedSheetName =
      String(sheetName || 'data')
        .replace(/[\\/?*[\]:]/g, ' ')
        .trim()
        .slice(0, 31) || 'data';
    XLSX.utils.book_append_sheet(workbook, worksheet, normalizedSheetName);
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    this.saveAsExcelFile(excelBuffer, fileName);
  }

  static saveAsExcelFile(buffer, fileName) {
    const data = new Blob([buffer], {
      type: EXCEL_TYPE_WITH_CHARSET,
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
  }
}
